import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { findMachinesByQr, normalizeQr } from './machines'

export interface ScannedMachineDraft {
  id: string
  name: string
  /** Exact QR payload as printed on the machine */
  qrPayload: string
  /** Muscle-diagram photo for later AI tagging */
  muscleImage: Blob
  muscleImageMime: string
  createdAt: number
}

interface ScannerDB extends DBSchema {
  drafts: {
    key: string
    value: ScannedMachineDraft
    indexes: { 'by-created': number; 'by-qr': string }
  }
}

const DB_NAME = 'myofade-scanner'
const DB_VERSION = 1

let dbPromise: Promise<IDBPDatabase<ScannerDB>> | null = null

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<ScannerDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const store = db.createObjectStore('drafts', { keyPath: 'id' })
        store.createIndex('by-created', 'createdAt')
        store.createIndex('by-qr', 'qrPayload')
      },
    })
  }
  return dbPromise
}

/** Test helper - closes and forgets the shared scanner DB connection. */
export async function resetScannerDbForTests(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise
    db.close()
  }
  dbPromise = null
  if (typeof indexedDB !== 'undefined') {
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase(DB_NAME)
      const done = () => resolve()
      req.onsuccess = done
      req.onerror = done
      req.onblocked = done
    })
  }
}

export async function addScannedMachine(
  draft: Omit<ScannedMachineDraft, 'id' | 'createdAt'> & {
    id?: string
    createdAt?: number
  },
): Promise<ScannedMachineDraft> {
  const db = await getDb()
  const record: ScannedMachineDraft = {
    id: draft.id ?? crypto.randomUUID(),
    name: draft.name.trim(),
    qrPayload: normalizeQr(draft.qrPayload),
    muscleImage: draft.muscleImage,
    muscleImageMime: draft.muscleImageMime,
    createdAt: draft.createdAt ?? Date.now(),
  }
  await db.put('drafts', record)
  return record
}

export async function listScannedMachines(): Promise<ScannedMachineDraft[]> {
  const db = await getDb()
  const all = await db.getAllFromIndex('drafts', 'by-created')
  return all.reverse()
}

export async function findScannedByQr(raw: string): Promise<ScannedMachineDraft[]> {
  const db = await getDb()
  const key = normalizeQr(raw)
  return db.getAllFromIndex('drafts', 'by-qr', key)
}

export async function deleteScannedMachine(id: string): Promise<void> {
  const db = await getDb()
  await db.delete('drafts', id)
}

export async function clearScannedMachines(): Promise<void> {
  const db = await getDb()
  await db.clear('drafts')
}

export interface DuplicateScanWarning {
  /** Matching drafts already in the scanner DB */
  drafts: ScannedMachineDraft[]
  /** Match in the shipped MACHINE_CATALOG, if any */
  catalogName?: string
  /** All catalog workout names for this QR (one sticker, multiple configs). */
  catalogNames?: string[]
}

/** Warn-only: same QR may still be saved again (one machine, two workouts). */
export async function checkDuplicateScan(raw: string): Promise<DuplicateScanWarning | null> {
  const drafts = await findScannedByQr(raw)
  const catalog = findMachinesByQr(raw)
  if (!drafts.length && !catalog.length) return null
  return {
    drafts,
    catalogName: catalog[0]?.name,
    catalogNames: catalog.length ? catalog.map((m) => m.name) : undefined,
  }
}
